import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { BarChart, Bell, LogOut, Menu, Settings, TrendingUp, Wallet, X } from "lucide-react";
import ThemeToggle from "@/components/theme-toggle";
import { useState } from "react";

const portfolioData = [
  { name: "Tesla", amount: "$2,450", change: "+12.5%", icon: "⚡" },
  { name: "SpaceX", amount: "$1,800", change: "+8.3%", icon: "🚀" },
  { name: "Starlink", amount: "$950", change: "+5.2%", icon: "🛰️" },
  { name: "Neuralink", amount: "$600", change: "+3.1%", icon: "🧠" },
];

const recentUpdates = [
  { id: 1, title: "Tesla Q4 Earnings", category: "Tesla", date: "Today" },
  { id: 2, title: "SpaceX Flight 7 Success", category: "SpaceX", date: "Yesterday" },
  { id: 3, title: "Neuralink Patient 2", category: "Innovation", date: "2 days ago" },
];

const notifications = [
  { id: 1, message: "Your monthly rewards have been issued: 0.05 BTC", type: "success" },
  { id: 2, message: "New investment opportunity: Premium tier exclusive", type: "info" },
  { id: 3, message: "Live Q&A in 2 hours with Tesla engineer", type: "info" },
];

export default function Dashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <motion.div
        className={`border-r border-border transition-all duration-300 ${
          sidebarOpen ? "w-64" : "w-20"
        } hidden md:block bg-card/50`}
        animate={{ width: sidebarOpen ? "16rem" : "5rem" }}
      >
        <div className="p-6 border-b border-border flex items-center justify-between">
          {sidebarOpen && <h2 className="font-semibold">MuskConnect</h2>}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1 hover:bg-border rounded">
            <Menu className="h-5 w-5" />
          </button>
        </div>

        <nav className="p-4 space-y-2">
          {[
            { icon: BarChart, label: "Overview" },
            { icon: Wallet, label: "Portfolio" },
            { icon: TrendingUp, label: "Analytics" },
            { icon: Settings, label: "Settings" },
          ].map((item, idx) => (
            <motion.button
              key={idx}
              whileHover={{ x: 4 }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded hover:bg-border transition-colors text-left"
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {sidebarOpen && <span className="text-sm">{item.label}</span>}
            </motion.button>
          ))}
        </nav>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-40">
          <div className="px-4 py-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <div className="flex items-center gap-4 md:hidden">
              <button onClick={() => setSidebarOpen(!sidebarOpen)}>
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              <h1 className="text-lg font-semibold">Dashboard</h1>
            </div>
            <h1 className="hidden md:block text-lg font-semibold">Welcome back, Victory</h1>

            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative p-2 hover:bg-card rounded transition-colors"
              >
                <Bell className="h-5 w-5" />
                <span className="absolute top-1 right-1 h-2 w-2 bg-foreground rounded-full" />
              </motion.button>
              <ThemeToggle />
              <Button variant="outline" size="sm" className="border-border text-foreground hover:bg-card">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>

          {/* Notifications Dropdown */}
          {notificationsOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-t border-border bg-card p-4 space-y-3"
            >
              {notifications.map((notif) => (
                <div key={notif.id} className="p-3 bg-background border border-border rounded text-sm">
                  {notif.message}
                </div>
              ))}
            </motion.div>
          )}
        </div>

        {/* Dashboard Content */}
        <div className="flex-1 overflow-auto">
          <div className="px-4 py-8 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
            <motion.div
              className="space-y-8"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Portfolio Overview */}
              <motion.div variants={itemVariants}>
                <h2 className="text-3xl font-medium mb-6">Your Portfolio</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {portfolioData.map((item, idx) => (
                    <motion.div
                      key={idx}
                      variants={itemVariants}
                      whileHover={{ y: -5 }}
                      className="border border-border p-6 hover:border-foreground transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="text-3xl">{item.icon}</div>
                        <span className="text-xs text-green-600 font-medium bg-green-50 dark:bg-green-950 px-2 py-1 rounded">
                          {item.change}
                        </span>
                      </div>
                      <h3 className="font-medium text-foreground mb-2">{item.name}</h3>
                      <p className="text-xl font-semibold text-foreground">{item.amount}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Quick Stats */}
              <motion.div variants={itemVariants}>
                <h3 className="text-2xl font-medium mb-6">Quick Stats</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { label: "Total Value", value: "$5,800", icon: "💰" },
                    { label: "Monthly Returns", value: "+$342", icon: "📈" },
                    { label: "Reward Points", value: "1,250 pts", icon: "⭐" },
                  ].map((stat, idx) => (
                    <motion.div
                      key={idx}
                      variants={itemVariants}
                      className="border border-border p-6"
                    >
                      <div className="text-2xl mb-2">{stat.icon}</div>
                      <p className="text-muted-foreground text-sm mb-2">{stat.label}</p>
                      <p className="text-2xl font-medium text-foreground">{stat.value}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Recent Updates */}
              <motion.div variants={itemVariants}>
                <h3 className="text-2xl font-medium mb-6">Latest Updates</h3>
                <div className="space-y-3">
                  {recentUpdates.map((update, idx) => (
                    <motion.div
                      key={idx}
                      variants={itemVariants}
                      className="border border-border p-4 hover:bg-card transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-foreground">{update.title}</h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            {update.category} • {update.date}
                          </p>
                        </div>
                        <span className="text-xs uppercase tracking-widest text-muted-foreground">
                          {update.category}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
